import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { X, ChevronUp, ChevronDown, ZoomIn, ZoomOut, Mail } from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";
import { Icon } from "@iconify/react";

// Define base URL and static size chart URLs for fallback
const SERVER_BASE_URL = "https://admin.monsinidress.com";

// Default data structure
const defaultData = {
	PROM: {
		years: [2027, 2026, 2025],
		sizeChartImage:
			"https://azuredress.com/static/media/sizechart.ea2456da397131b84a82.jpeg",
	},
};

// Helper function to construct full image URL
const constructImageUrl = (imagePath) => {
	if (!imagePath) return null;

	// If it's already a full URL, use it directly
	if (imagePath.startsWith("http")) {
		return imagePath;
	}

	// If it starts with a slash, remove it to avoid double slashes
	const cleanPath = imagePath.startsWith("/")
		? imagePath.substring(1)
		: imagePath;

	// Construct full URL
	return `${SERVER_BASE_URL}/${cleanPath}`;
};

export default function Footer() {
	const [openMenu, setOpenMenu] = useState(null);
	const [showSizeChart, setShowSizeChart] = useState("");
	const [zoomed, setZoomed] = useState(false);
	const [isZoomActive, setIsZoomActive] = useState(false);
	const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });
	const [footerData, setFooterData] = useState(defaultData);
	const [email, setEmail] = useState("");
	const [emailError, setEmailError] = useState("");
	const [subscribeMessage, setSubscribeMessage] = useState(null);
	const [capVal, setCapVal] = useState(null);
	const [status, setStatus] = useState(null);
	const [showRecaptcha, setShowRecaptcha] = useState(false);

	const imageRef = useRef(null);

	useEffect(() => {
		const fetchProducts = async () => {
			try {
				const response = await fetch(
					`https://admin.monsinidress.com/api/products/navbar`,
					{
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({ brand: "Azure" }),
					}
				);

				if (!response.ok) {
					throw new Error("Failed to fetch products");
				}
				const productsData = await response.json();
				console.log("Fetched footer data:", productsData);

				if (productsData.data && Object.keys(productsData.data).length > 0) {
					// Transform the API data to match our expected structure
					const transformedData = {};

					// Handle Prom data (from data.Prom)
					if (productsData.data.Prom) {
						const sizeChartImage =
							constructImageUrl(productsData.data.Prom.sizeChartImage) ||
							defaultData.PROM.sizeChartImage;

						transformedData.PROM = {
							years: productsData.data.Prom.years || [],
							sizeChartImage: sizeChartImage,
						};
					}

					// Handle other categories if provided by API
					if (productsData.data.Bridal) {
						const bridalSizeChartImage =
							constructImageUrl(productsData.data.Bridal.sizeChartImage) ||
							defaultData.BRIDAL.sizeChartImage;

						transformedData.BRIDAL = {
							years: productsData.data.Bridal.years || [],
							sizeChartImage: bridalSizeChartImage,
						};
					}

					if (productsData.data.Evening) {
						const eveningSizeChartImage =
							constructImageUrl(productsData.data.Evening.sizeChartImage) ||
							defaultData.EVENING.sizeChartImage;

						transformedData.EVENING = {
							years: productsData.data.Evening.years || [],
							sizeChartImage: eveningSizeChartImage,
						};
					}

					// Merge with default data for other categories
					const mergedData = {
						...defaultData,
						...transformedData,
					};

					setFooterData(mergedData);
				} else {
					// Fallback to default data if API response is empty
					setFooterData(defaultData);
				}
			} catch (err) {
				console.error("Error fetching footer data:", err);
				setFooterData(defaultData);
			}
		};

		fetchProducts();
	}, []);

	const toggleMenu = (menu) => {
		setOpenMenu(openMenu === menu ? null : menu);
	};

	const handleSizeChart = (category) =>
		setShowSizeChart(category.toUpperCase());

	// Helper to get the size chart image URL
	const getSizeChartImage = (categoryKey) => {
		const categoryData = footerData[categoryKey];

		if (categoryData && categoryData.sizeChartImage) {
			return categoryData.sizeChartImage;
		}

		// Fallback to default images
		switch (categoryKey) {
			case "PROM":
				return defaultData.PROM.sizeChartImage;

			default:
				return defaultData.PROM.sizeChartImage;
		}
	};

	const getDropdownClasses = (menuName) => {
		const baseClasses =
			"ml-4 mt-2 flex flex-col gap-1 overflow-hidden transition-all duration-300 ease-in-out";
		const isOpen = openMenu === menuName;
		const heightClass = isOpen ? "max-h-60 opacity-100" : "max-h-0 opacity-0";
		return `${baseClasses} ${heightClass}`;
	};

	// Zoom effect handlers (same as navbar)
	const handleMouseMove = (e) => {
		if (!imageRef.current) return;

		const rect = imageRef.current.getBoundingClientRect();
		const x = (e.nativeEvent.offsetX / rect.width) * 100;
		const y = (e.nativeEvent.offsetY / rect.height) * 100;
		setZoomPosition({ x, y });
	};

	const handleMouseEnter = () => {
		setIsZoomActive(true);
	};

	const handleMouseLeave = () => {
		setIsZoomActive(false);
	};

	const handleSubscribe = async () => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

		setEmailError("");
		setSubscribeMessage(null);
		setStatus(null);

		if (!email) {
			setEmailError("Email is required");
			return;
		} else if (!emailRegex.test(email)) {
			setEmailError("Please enter a valid email address");
			return;
		}

		if (!showRecaptcha) {
			setShowRecaptcha(true);
			return;
		}

		if (!capVal) {
			setStatus("recaptcha-error");
			return;
		}

		try {
			const payload = {
				brandName: "Azure",
				email: email,
			};

			const response = await fetch(
				`https://admin.monsinidress.com/api/subscriber`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(payload),
				}
			);

			if (!response.ok) {
				console.log(response);
				const errorData = await response
					.json()
					.catch(() => ({ message: "Subscription failed." }));
				throw new Error(
					errorData.message || "Subscription failed. Please try again."
				);
			}

			setEmailError("");
			setSubscribeMessage("Thank you for subscribing! Check your inbox.");
			setEmail("");
			setCapVal(null);
			setShowRecaptcha(false);
			setStatus(null);
			setTimeout(() => setSubscribeMessage(null), 4000);
		} catch (error) {
			console.error("Subscription API Error:", error);
			setSubscribeMessage(
				error.message || "An unknown error occurred during subscription."
			);
			setTimeout(() => setSubscribeMessage(null), 5000);
		}
	};

	// Get the image source for the currently selected size chart category
	const sizeChartImageSrc = getSizeChartImage(showSizeChart);

	return (
		<>
			{/* 🧭 SIZE CHART MODAL - UPDATED with navbar logic */}
			{showSizeChart && (
				<div className="fixed inset-0 h-screen bg-black bg-opacity-70 flex items-center justify-center z-[1200] p-4">
					<div className="absolute top-4 right-4 gap-3 z-10 flex justify-between items-center">
						{/* Zoom Button */}
						<button
							onClick={() => setZoomed((prev) => !prev)}
							className="bg-white/10 hover:bg-white/20 text-white p-2 rounded-full transition">
							{zoomed ? (
								<ZoomOut className="w-7 h-7" />
							) : (
								<ZoomIn className="w-7 h-7" />
							)}
						</button>

						{/* Close Button */}
						<button
							onClick={() => {
								setShowSizeChart("");
								setZoomed(false);
								setIsZoomActive(false);
							}}
							className="bg-white/10 hover:bg-white/20 text-white p-2 rounded-full transition">
							<X className="w-7 h-7" />
						</button>
					</div>

					<div className="w-full bg-transparent flex flex-col justify-center items-center gap-5 relative">
						<div
							className={`w-[100%] h-full max-h-[90vh] overflow-x-auto overflow-y-hidden flex justify-center ${
								zoomed ? "cursor-grab" : ""
							}`}>
							{/* Zoomable Image Container */}
							<div
								ref={imageRef}
								className="w-[70%] max-h-[600px] relative overflow-hidden rounded-2xl bg-gray-50"
								onMouseMove={handleMouseMove}
								onMouseEnter={handleMouseEnter}
								onMouseLeave={handleMouseLeave}>
								{/* Main Image */}
								<img
									src={sizeChartImageSrc}
									alt={`${showSizeChart} Size Chart`}
									className={`transition-transform duration-300 w-full h-full object-contain px-5 ${
										zoomed ? "scale-110" : "scale-100"
									}`}
									style={{
										transformOrigin: "center center",
										minWidth: zoomed ? "130%" : "auto",
									}}
									onError={(e) => {
										console.error(
											`Failed to load image for ${showSizeChart}:`,
											sizeChartImageSrc
										);
										// Fallback to default image if loading fails
										e.target.src = defaultData.PROM.sizeChartImage;
									}}
								/>

								{/* Zoom Overlay */}
								<div
									className="absolute inset-0 transition-opacity duration-300"
									style={{
										opacity: isZoomActive && !zoomed ? 1 : 0,
										backgroundImage: `url(${sizeChartImageSrc})`,
										backgroundSize: "200%",
										backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
										pointerEvents: "none",
									}}
								/>

								{/* Zoom Icon */}
								<div
									className={`absolute bottom-4 right-4 bg-blue-600 text-white w-[50px] flex items-center justify-center px-0 py-[14px] rounded-full text-sm transition-all duration-300 ${
										isZoomActive && !zoomed ? "opacity-100" : "opacity-0"
									}`}>
									<ZoomIn className="w-5 h-5" />
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Custom Subscription Message */}
			{subscribeMessage && (
				<div
					className={`fixed bottom-4 right-4 text-white p-3 rounded-lg shadow-xl z-[1202] flex items-center gap-2 ${
						subscribeMessage.includes("failed") ? "bg-red-600" : "bg-green-500"
					}`}>
					<Mail className="w-5 h-5" />
					{subscribeMessage}
				</div>
			)}

			{/* FOOTER */}
			<div className="w-full bg-[#1f4c94] overflow-x-hidden">
				<div className="w-full md:px-10 px-6 lg:px-16 grid lg:grid-cols-4 md:grid-cols-2 gap-5 border-b">
					{/* Newsletter */}
					<div className="flex justify-center items-start text-white flex-col lg:px-10 md:px-4 md:py-2 lg:py-6">
						<h2 className="text-3xl font-lora my-4">Let's get in Touch</h2>
						<p className="font-poppins text-[14px]">
							Sign up for our newsletter and get news about latest products and
							discounts
						</p>

						<input
							type="text"
							placeholder="Enter Your Email Address..."
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							className="text-white placeholder:text-white font-poppins px-3 w-full outline-none border-2 bg-inherit text-[16px] py-2 rounded-lg my-2 border-white hover:bg-white hover:bg-opacity-25 transition-all duration-300 focus:bg-white focus:bg-opacity-25"
						/>
						{emailError && (
							<p className="text-white text-sm bg-red-600 p-1 rounded mt-1">
								{emailError}
							</p>
						)}

						{/* CORRECTED: ReCAPTCHA Integration */}
						{showRecaptcha && (
							<div>
								<ReCAPTCHA
									// sitekey="6LfijiksAAAAADpuHlen7pWoP5IyPDMFvpm0QBJA"
									sitekey="6Lc8fiwsAAAAAEnGQbIZaoT-y5EPeaK87c03G494"
									onChange={(val) => setCapVal(val)}
									// secret Key  6Lc8fiwsAAAAAHJaPt3AnrCf44BiNu6mbl84fIol
								/>
								{status === "recaptcha-error" && (
									<p className="mt-2 text-sm text-red-600 font-medium">
										Please complete the reCAPTCHA verification
									</p>
								)}
							</div>
						)}

						<button
							onClick={handleSubscribe}
							className="mt-4 bg-white text-[#1f4c94] font-semibold py-1.5 px-4 rounded hover:bg-opacity-90 transition w-full">
							{showRecaptcha ? "Complete Subscription" : "Subscribe"}
						</button>
					</div>

					{/* Dynamic Dropdowns */}
					<div className="text-white font-Jost-Regular md:px-5 lg:px-5 flex flex-col gap-2 py-6 md:py-10 lg:py-14">
						<h2 className="font-lora mb-2 font-medium text-[20px]">
							Collections
						</h2>
						{Object.keys(footerData).map((categoryName) => {
							const menuKey = categoryName.toLowerCase();
							const categoryData = footerData[categoryName];
							const yearsList = categoryData?.years || [];

							return (
								<div key={categoryName} className="font-poppins">
									<button
										onClick={() => toggleMenu(menuKey)}
										className="flex justify-between items-center w-full py-1 px-2 rounded transition-all duration-300 hover:bg-white hover:bg-opacity-25 hover:px-2">
										<span>{categoryName}</span>
										<span>
											{openMenu === menuKey ? <ChevronUp /> : <ChevronDown />}
										</span>
									</button>
									<div className={getDropdownClasses(menuKey)}>
										{yearsList.map((year) => (
											<Link
												key={year}
												to={`/${categoryName.toLowerCase()}/${year}`}
												className="py-1 px-2 rounded transition-all duration-300 hover:bg-white hover:bg-opacity-25 hover:px-3 block">
												{year} Collection
											</Link>
										))}
										<button
											onClick={() => handleSizeChart(categoryName)}
											className="py-1 px-2 rounded transition-all duration-300 hover:bg-white hover:bg-opacity-25 hover:px-3 w-full text-left">
											Size Chart
										</button>
									</div>
								</div>
							);
						})}
					</div>

					{/* Contact Section */}
					<div className="text-white font-Jost-Regular md:px-5 lg:px-5 flex flex-col gap-2 py-6 md:py-10 lg:py-14">
						<h2 className="font-lora mb-2 font-medium text-[20px]">
							Contact Links
						</h2>
						<p className="font-poppins">
							<Link
								to="/about"
								className="block py-1 px-2 rounded transition-all duration-300 hover:bg-white hover:bg-opacity-25 hover:px-2">
								About Us
							</Link>
						</p>
						<p className="font-poppins">
							<Link
								to="/contact"
								className="block py-1 px-2 rounded transition-all duration-300 hover:bg-white hover:bg-opacity-25 hover:px-2">
								Contact Us
							</Link>
						</p>
						<p className=" border-[1px] border-white rounded-md w-fit p-2">
							<Link
								to="https:market.azuredress.com/"
								className="flex items-center gap-1 text-white hover:text-gray-300 transition">
								<Icon icon="qlementine-icons:user-16" width="20" height="20" />
								<span className="text-sm font-medium">RETAILER LOGIN</span>
							</Link>
						</p>
					</div>

					{/* Social Section */}
					<div className="text-white md:px-5 lg:px-5 flex flex-col gap-2 py-6 md:py-10 lg:py-14">
						<h2 className="font-lora mb-2 font-medium text-[20px]">
							Social Links
						</h2>
						<p className="font-poppins hover:bg-white hover:bg-opacity-25 py-1 px-2 rounded transition-all duration-300 cursor-default">
							<Link
								to={
									"https://www.instagram.com/azuredress?igsh=MXhoNzZ2N3hqbjgyeA=="
								}>
								Instagram
							</Link>
						</p>
					</div>
				</div>
			</div>
		</>
	);
}
